import { FC, useEffect, useMemo } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { globalAction } from 'common/actions';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { FUNDS_VALUATIONS_SUMMARY_TITLE } from 'common/constants/funds';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomScalarSpreadsheet, MessageBox, SectionHeading } from 'components';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import {
  VALUATIONS_SUMMARY_CONTAINER_CLASSNAME,
  VALUATIONS_SUMMARY_NO_PUBLISHED_ALLOCATIONS_FOUND,
  VALUATIONS_SUMMARY_NO_VALUATION_METHODS_PROVIDED,
} from 'pages/Funds/common/constants/valuationsSummary';
import { InformationMessage } from 'pages/Funds/components';
import { useValuationsSummarySheet } from 'pages/Funds/hooks';
import { useGetFundAllocations, useGetFundSummary } from 'services/hooks/funds';
import { getArrayValue, getBooleanValue, getObjectValue } from 'utilities';
import { ValuationsSummaryProps } from './types';

const ValuationsSummary: FC<ValuationsSummaryProps> = props => {
  const { fund, measurementDate } = props;

  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);

  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const { data: fundAllocations, isFetching, isLoading } = useGetFundAllocations({ fund, measurementDate });

  const {
    data: fundSummary,
    isFetching: isFetchingSummary,
    isLoading: isLoadingSummary,
  } = useGetFundSummary({ fund, measurementDate });

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: fund?.currency?.toString(),
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const { spreadsheet: valuationsSummarySheet } = useValuationsSummarySheet({
    format,
    fund,
    fundAllocations,
    measurementDate,
  });

  const spreadsheets = useMemo(
    () => (valuationsSummarySheet?.yLength ? [valuationsSummarySheet] : []),
    [valuationsSummarySheet]
  );

  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  // Create valuationsSummarySheet props
  const valuationsSummarySheetProps = useMemo(() => {
    if (valuationsSummarySheet) {
      return {
        ...valuationsSummarySheet,
        data: getArrayValue(valuationsSummarySheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: valuationsSummarySheet,
        workbook,
      } as unknown as typeof ScalarSpreadsheet;
    }

    return null;
  }, [format, formatDispatch, onChange, valuationsSummarySheet, workbook]);

  // Direct investments from fund summary
  const directInvestments = useMemo(() => getArrayValue(fundSummary?.companies), [fundSummary]);

  const shouldHideValuationsSummary = useMemo(
    () => areFundInvestmentsEnabled && isEmpty(directInvestments),
    [directInvestments, areFundInvestmentsEnabled]
  );

  // If at least one Allocation has only Custom Ownerships, display the message
  const shouldDisplayNoValuationMethodsMessage = useMemo(
    () =>
      getBooleanValue(
        fundAllocations?.allocations?.some(allocationItem => allocationItem?.allocation?.has_only_custom_ownerships)
      ),
    [fundAllocations]
  );

  // Show Loading Progress
  useEffect(() => {
    dispatch?.(globalAction.showLoadingProgress(isLoading || isLoadingSummary));
  }, [dispatch, isLoading, isLoadingSummary]);

  // Loading Grid Skeleton while fetching data
  if (isLoading || isLoadingSummary) {
    return <GridSkeleton maxColumns={10} maxRows={4} />;
  }

  // Hide when fund investments are enabled and no direct investments exist
  if (shouldHideValuationsSummary) {
    return null;
  }

  // Show message if no published Allocations found
  if (isEmpty(fundAllocations?.allocations)) {
    return <MessageBox title={VALUATIONS_SUMMARY_NO_PUBLISHED_ALLOCATIONS_FOUND} fullWidth={false} />;
  }

  return (
    <>
      {valuationsSummarySheetProps && valuationsSummarySheet && !isEmpty(fundAllocations?.allocations) && (
        <CustomCompactContainer className={VALUATIONS_SUMMARY_CONTAINER_CLASSNAME}>
          <Grid size={{ xs: 12 }}>
            <SectionHeading leftAligned title={FUNDS_VALUATIONS_SUMMARY_TITLE} />

            {/* Render the scalar spreadsheet with the workbook context */}
            <WorkbookContext.Provider value={workbookContextValue}>
              <CustomScalarSpreadsheet {...valuationsSummarySheetProps} isLoading={isFetching || isFetchingSummary} />
            </WorkbookContext.Provider>
          </Grid>

          <InformationMessage
            displayMessage={shouldDisplayNoValuationMethodsMessage}
            message={VALUATIONS_SUMMARY_NO_VALUATION_METHODS_PROVIDED}
            noMargin
          />
        </CustomCompactContainer>
      )}
    </>
  );
};

export default ValuationsSummary;
